/**
 * Sync/Hub Flow UI visibility JSON (⋮ actions + Properties subsections).
 * Catalog from global.app_lookup (per Sync/Hub). Choices on subscription_licensed_flow.screen_ui.
 */

/** Map lookup rows → { key, label }[]. */
export function defsFromLookupRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows
    .map((r) => ({
      key: String(r.code || r.key || '').trim(),
      label: String(r.label || r.code || '').trim(),
    }))
    .filter((d) => d.key);
}

/**
 * Resolve SYNC | HUB from master flow path using FLOW_UI_KIND_MATCH (longest prefix).
 */
export function resolveFlowUiKind(flowPath, lookups) {
  const path = String(flowPath || '').trim();
  const rows = Array.isArray(lookups?.FLOW_UI_KIND_MATCH) ? lookups.FLOW_UI_KIND_MATCH : [];
  let best = null;
  rows.forEach((r) => {
    const prefix = String(r.code || '').trim();
    if (!prefix || !path.startsWith(prefix)) return;
    if (!best || prefix.length > String(best.code || '').length) {
      best = r;
    }
  });
  const kind = String(best?.label || 'SYNC')
    .trim()
    .toUpperCase();
  return kind === 'HUB' ? 'HUB' : 'SYNC';
}

function dedupeDefs(list) {
  const seen = new Set();
  return (list || []).filter((d) => {
    if (!d?.key || seen.has(d.key)) return false;
    seen.add(d.key);
    return true;
  });
}

/**
 * Catalog for a master flow path = COMMON + Sync-only or Hub-only.
 * @returns {{ kind: 'SYNC'|'HUB', actions: Array, sections: Array }}
 */
export function flowUiCatalogFromLookups(lookups, flowPath) {
  const kind = resolveFlowUiKind(flowPath, lookups);
  const commonActions = defsFromLookupRows(lookups?.FLOW_UI_ACTION_COMMON);
  const commonSections = defsFromLookupRows(lookups?.FLOW_UI_SECTION_COMMON);
  const kindActions = defsFromLookupRows(
    kind === 'HUB' ? lookups?.FLOW_UI_ACTION_HUB : lookups?.FLOW_UI_ACTION_SYNC
  );
  const kindSections = defsFromLookupRows(
    kind === 'HUB' ? lookups?.FLOW_UI_SECTION_HUB : lookups?.FLOW_UI_SECTION_SYNC
  );
  return {
    kind,
    actions: dedupeDefs([...commonActions, ...kindActions]),
    sections: dedupeDefs([...commonSections, ...kindSections]),
  };
}

/** Full catalog (common + sync + hub) — parse/save without a path. */
export function flowUiCatalogAllFromLookups(lookups) {
  return {
    kind: 'ALL',
    actions: dedupeDefs([
      ...defsFromLookupRows(lookups?.FLOW_UI_ACTION_COMMON),
      ...defsFromLookupRows(lookups?.FLOW_UI_ACTION_SYNC),
      ...defsFromLookupRows(lookups?.FLOW_UI_ACTION_HUB),
    ]),
    sections: dedupeDefs([
      ...defsFromLookupRows(lookups?.FLOW_UI_SECTION_COMMON),
      ...defsFromLookupRows(lookups?.FLOW_UI_SECTION_SYNC),
      ...defsFromLookupRows(lookups?.FLOW_UI_SECTION_HUB),
    ]),
  };
}

const allTrue = (defs) =>
  Object.fromEntries((defs || []).map(({ key }) => [key, true]));

export function defaultMasterFlowUiConfig(catalog) {
  const actions = catalog?.actions || [];
  const sections = catalog?.sections || [];
  return {
    version: 1,
    actions: allTrue(actions),
    sections: allTrue(sections),
  };
}

function mergeFlags(defaults, incoming) {
  const out = { ...defaults };
  if (incoming && typeof incoming === 'object') {
    Object.keys(incoming).forEach((key) => {
      if (typeof incoming[key] === 'boolean') {
        out[key] = incoming[key];
      }
    });
  }
  return out;
}

export function parseMasterFlowUiConfig(raw, catalog) {
  const defaults = defaultMasterFlowUiConfig(catalog);
  if (raw == null || String(raw).trim() === '') {
    return defaults;
  }
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return defaults;
    }
    if (parsed.version == null && !parsed.actions && !parsed.sections) {
      return defaults;
    }
    return {
      version: 1,
      actions: mergeFlags(defaults.actions, parsed.actions),
      sections: mergeFlags(defaults.sections, parsed.sections),
    };
  } catch {
    return defaults;
  }
}

export function serializeMasterFlowUiConfig(config, catalog) {
  const normalized = parseMasterFlowUiConfig(config, catalog);
  return JSON.stringify(normalized);
}

export function shouldBypassMasterFlowUiRestrictions({ hasFullAccess, isCdxUser } = {}) {
  if (hasFullAccess === true) return true;
  if (isCdxUser === true) return true;
  try {
    return localStorage.getItem('cdx_user') === 'true';
  } catch {
    return false;
  }
}

export function isMfUiActionEnabled(configOrRaw, actionKey, options = {}) {
  if (shouldBypassMasterFlowUiRestrictions(options)) return true;
  const cfg = parseMasterFlowUiConfig(configOrRaw);
  return cfg.actions[actionKey] !== false;
}

export function isMfUiSectionEnabled(configOrRaw, sectionKey, options = {}) {
  if (shouldBypassMasterFlowUiRestrictions(options)) return true;
  const cfg = parseMasterFlowUiConfig(configOrRaw);
  return cfg.sections[sectionKey] !== false;
}

export function uiConfigForMasterFlow(masterFlows, mfId, options = {}) {
  if (shouldBypassMasterFlowUiRestrictions(options)) {
    return defaultMasterFlowUiConfig(options.catalog);
  }
  const id = mfId != null ? String(mfId) : '';
  const byMf = options.licensedFlowUiByMfId;
  if (id && byMf && typeof byMf === 'object' && Object.prototype.hasOwnProperty.call(byMf, id)) {
    return parseMasterFlowUiConfig(byMf[id], options.catalog);
  }
  return defaultMasterFlowUiConfig(options.catalog);
}
